import { createForm, useStore } from "@tanstack/solid-form";
import { AiFillPlusCircle } from "solid-icons/ai";
import { IoPencilSharp, IoTrash } from "solid-icons/io";
import {
  type Component,
  createMemo,
  createSignal,
  For,
  type JSX,
  type ParentComponent,
  Show,
} from "solid-js";
import { produce } from "solid-js/store";
import { toast } from "solid-sonner";
import { z } from "zod";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
  Input,
} from "@/components/ui/input";
import { Switch, SwitchControl, SwitchThumb } from "@/components/ui/switch";
import { TextArea } from "@/components/ui/textarea";
import {
  TextFieldDescription,
  TextFieldErrorMessage,
  TextFieldLabel,
  TextFieldRoot,
} from "@/components/ui/textfield";
import { devLog } from "@/libs/dev-log";
import {
  type OnboardingExperience,
  useOnboardingStore,
} from "../../context/OnboardingContext";
import { OnboardingDescription, OnboardingTitle } from "../shared/text";
import { sortExperienceByStartDate } from "../utils";

const ExperiencePreviewItem: Component<OnboardingExperience> = (props) => {
  const { t } = useI18n();
  const [, setStore] = useOnboardingStore();

  const handleDelete = () => {
    setStore(
      produce((draft) => {
        const updatedExperience = draft.experience.value.filter(
          (experience) => experience.temp_id !== props.temp_id,
        );
        draft.experience.value = sortExperienceByStartDate(updatedExperience);
      }),
    );
  };

  const startDate = createMemo(() =>
    props.start_date ? new Date(props.start_date).toLocaleDateString() : "??",
  );
  const endDate = createMemo(() => {
    if (props.end_date) {
      if (props.is_current) {
        return "Present";
      }
      return new Date(props.end_date).toLocaleDateString();
    }
    return "Present";
  });

  return (
    <Card class="flex flex-col gap-4 shrink-0 w-full bg-white border-primary/10 rounded-lg relative shadow-none min-h-64 max-w-md min-w-md">
      <CardHeader class="max-w-xs">
        <CardTitle>{props.job_title}</CardTitle>
        <CardDescription>
          {props.company_name} | {startDate()} - {endDate()}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Show when={props.description}>
          <p class="text-sm text-gray-500">{props.description}</p>
        </Show>
        <div class="flex gap-2 items-center justify-center absolute top-4 right-4">
          <ExperienceDialog
            experience={{
              temp_id: props.temp_id,
              company_name: props.company_name,
              job_title: props.job_title,
              start_date: props.start_date ? new Date(props.start_date) : null,
              end_date: props.end_date ? new Date(props.end_date) : null,
              is_current: props.is_current,
              description: props.description,
            }}
          >
            <Button variant="outline">
              <IoPencilSharp class="w-4 h-4" />
              <span class="sr-only">Edit</span>
            </Button>
          </ExperienceDialog>
          <Button variant="outline" onClick={handleDelete}>
            <IoTrash class="w-4 h-4" />
            <span class="sr-only">{t("common.delete")}</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const EmptyExperiencePreviewItem = () => {
  const { t } = useI18n();
  return (
    <ExperienceDialog>
      <Card class="flex gap-4 max-w-md w-full bg-white/60 group cursor-pointer hover:bg-white border-dashed border-gray-400 rounded-lg relative min-h-64 shadow-none transition-colors duration-300">
        <CardContent class="flex flex-col items-start justify-center w-full">
          <Button variant="link" class="p-0">
            <AiFillPlusCircle class="w-8 h-8 group-hover:text-primary/70" />
            <span class="sr-only">{t("common.add")}</span>
          </Button>
          <p class="text-2xl text-gray-500 text-center mt-2">
            {t("onboarding.steps.experience.addExperience")}
          </p>
        </CardContent>
      </Card>
    </ExperienceDialog>
  );
};
export const Experience = () => {
  const { t } = useI18n();
  const [store] = useOnboardingStore();
  const sortedExperience = createMemo(() =>
    sortExperienceByStartDate(store.experience.value),
  );
  return (
    <div class="flex flex-col gap-8 w-full">
      <div class="flex flex-col gap-4">
        <OnboardingTitle>
          {t("onboarding.steps.experience.title")}
        </OnboardingTitle>
        <OnboardingDescription>
          {t("onboarding.steps.experience.description")}
        </OnboardingDescription>
      </div>
      <div class="w-full relative">
        <div class="overflow-x-auto flex gap-4 w-full">
          <Show
            when={sortedExperience().length > 0}
            fallback={<EmptyExperiencePreviewItem />}
          >
            <For each={sortedExperience()}>
              {(experience) => (
                <ExperiencePreviewItem
                  temp_id={experience.temp_id}
                  company_name={experience.company_name}
                  start_date={experience.start_date}
                  end_date={experience.end_date}
                  job_title={experience.job_title}
                  is_current={!!experience.is_current}
                  description={experience.description}
                />
              )}
            </For>
            <Show when={sortedExperience().length > 0}>
              <div class="hidden lg:block absolute top-1/2 left-0 -translate-x-16 z-10">
                <ExperienceDialog>
                  <Button variant="link" class="p-0 w-fit h-fit group">
                    <AiFillPlusCircle class="w-12 h-12 group-hover:text-primary/70 transition-colors duration-300" />
                    <span class="sr-only">{t("common.add")}</span>
                  </Button>
                </ExperienceDialog>
              </div>
            </Show>
          </Show>
        </div>
        <div class="lg:hidden mt-4">
          <ExperienceDialog>
            <Button>{t("onboarding.steps.experience.addExperience")}</Button>
          </ExperienceDialog>
        </div>
      </div>
    </div>
  );
};

const jobsDialogSchema = z
  .object({
    job_title: z
      .string({ error: "Job title is required" })
      .min(1, { error: "Job title is required" }),
    company_name: z
      .string({ error: "Company name is required" })
      .min(1, { error: "Company name is required" }),
    start_date: z.date().min(1, { error: "Data di inizio obbligatoria" }),
    end_date: z.date().optional().or(z.null()),
    description: z.string().optional().or(z.null()),
    is_current: z.boolean().optional(),
  })
  .refine(
    (data) => {
      if (!data.is_current && !data.end_date) {
        return false;
      }
      return true;
    },
    { error: "Data di fine obbligatoria", path: ["end_date"] },
  );

const ExperienceDialog: ParentComponent<{
  children: JSX.Element;
  experience?: OnboardingExperience;
}> = (props) => {
  const [, setStore] = useOnboardingStore();
  const isEditing = props.experience !== undefined;
  const [isOpen, setIsOpen] = createSignal(false);
  const form = createForm(() => ({
    defaultValues: {
      ...props.experience,
      start_date: props.experience?.start_date
        ? props.experience.start_date
        : null,
      end_date: props.experience?.end_date ? props.experience.end_date : null,
    },
    schema: jobsDialogSchema,
    onSubmit: async ({ value }) => {
      const validatedData = jobsDialogSchema.safeParse(value);
      if (!validatedData.success) {
        devLog(validatedData.error.issues);
        return;
      }
      if (isEditing) {
        toast.success(
          t("common.toast.updated_success", {
            item: t("onboarding.steps.experience.form.jobTitle"),
          }),
        );
        setStore(
          produce((draft) => {
            const editedExperience = draft.experience.value.find(
              (experience) => experience.temp_id === props.experience?.temp_id,
            );
            if (editedExperience) {
              editedExperience.job_title = validatedData.data.job_title;
              editedExperience.company_name = validatedData.data.company_name;
              editedExperience.start_date = validatedData.data.start_date;
              editedExperience.end_date = validatedData.data.end_date ?? null;
              editedExperience.is_current =
                validatedData.data.is_current ?? false;
              editedExperience.description = validatedData.data.description;
              editedExperience.temp_id = props.experience!.temp_id!;
            }

            const updatedExperience = draft.experience.value.map(
              (experience) =>
                experience.temp_id === props.experience?.temp_id
                  ? editedExperience!
                  : experience,
            );
            draft.experience.value =
              sortExperienceByStartDate(updatedExperience);
          }),
        );
        // form.reset();
      } else {
        toast.success(
          t("common.toast.added_success", {
            item: t("onboarding.steps.experience.form.jobTitle"),
          }),
        );
        setStore(
          produce((draft) => {
            const newExperience = {
              ...validatedData.data,
              start_date: validatedData.data.start_date,
              end_date: validatedData.data.end_date ?? null,
              is_current: validatedData.data.is_current ?? false,
              temp_id: (draft.experience.value.length + 1).toString(),
            };
            const updatedExperience = [
              ...draft.experience.value,
              newExperience,
            ];

            draft.experience.value =
              sortExperienceByStartDate(updatedExperience);
          }),
        );
      }
      setIsOpen(false);
      form.reset();
    },
  }));

  const { t } = useI18n();

  const isCurrent = useStore(form.store, (state) => state.values.is_current);
  const endDate = useStore(form.store, (state) => state.values.end_date);
  const startDate = useStore(form.store, (state) => state.values.start_date);

  return (
    <Dialog
      open={isOpen()}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) {
          form.reset();
        }
      }}
    >
      <DialogTrigger class="lg:w-full">{props.children}</DialogTrigger>
      <DialogContent class="max-w-2xl min-h-[min(600px,90svh)] bg-card">
        <DialogHeader>
          <DialogTitle class="text-2xl font-medium">
            <Show
              when={isEditing}
              fallback={t("onboarding.steps.experience.addExperience")}
            >
              {t("onboarding.steps.experience.editExperience")}
            </Show>
          </DialogTitle>
        </DialogHeader>
        <div class="flex flex-col gap-4">
          <form.Field
            name="job_title"
            validators={{
              onChange: jobsDialogSchema.shape.job_title,
              onBlur: jobsDialogSchema.shape.job_title,
            }}
          >
            {(field) => (
              <FieldContainer>
                <FieldContainerLabel for="job_title">
                  {t("onboarding.steps.experience.form.jobTitle")}
                </FieldContainerLabel>
                <Input
                  id="job_title"
                  autocomplete="job_title"
                  name={field().name}
                  value={field().state.value ?? ""}
                  onBlur={field().handleBlur}
                  onInput={(e) => field().handleChange(e.currentTarget.value)}
                  onChange={(e) => field().handleChange(e.currentTarget.value)}
                />
                <FieldInfo
                  field={field()}
                  helpText={t("onboarding.steps.experience.form.jobTitleHelp")}
                />
              </FieldContainer>
            )}
          </form.Field>
          <form.Field
            name="company_name"
            validators={{
              onChange: jobsDialogSchema.shape.company_name,
              onBlur: jobsDialogSchema.shape.company_name,
            }}
          >
            {(field) => {
              return (
                <FieldContainer>
                  <FieldContainerLabel for="company_name">
                    {t("onboarding.steps.experience.form.company")}
                  </FieldContainerLabel>
                  <Input
                    autocomplete="company_name"
                    id="company_name"
                    name={field().name}
                    value={field().state.value ?? ""}
                    onBlur={field().handleBlur}
                    onChange={(e) =>
                      field().handleChange(e.currentTarget.value)
                    }
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                  />
                  <FieldInfo
                    field={field()}
                    helpText={t("onboarding.steps.experience.form.companyHelp")}
                  />
                </FieldContainer>
              );
            }}
          </form.Field>

          <div class="grid grid-cols-2 gap-4 w-full">
            <form.Field
              name="start_date"
              validators={{
                onChange: ({ value }) => {
                  if (value) {
                    if (endDate()) {
                      const startDateObj = value;
                      const endDateObj = endDate()!;
                      if (startDateObj > endDateObj) {
                        return t(
                          "onboarding.steps.experience.form.startDateHelp",
                        );
                      }
                    }
                  }
                },
                onBlur: ({ value }) => {
                  const validatedData =
                    jobsDialogSchema.shape.start_date.safeParse(value);
                  if (!validatedData.success) {
                    return validatedData.error.issues[0].message;
                  }
                  return undefined;
                },
              }}
            >
              {(field) => (
                <FieldContainer>
                  <FieldContainerLabel for="start_date">
                    {t("onboarding.steps.experience.form.startDate")}
                  </FieldContainerLabel>
                  <DatePicker
                    id="start_date"
                    value={field().state.value ?? undefined}
                    maxDate={endDate() ?? undefined}
                    onChange={(date) => {
                      if (date) {
                        field().handleChange(date);
                      }
                    }}
                    classList={{
                      "[&_button]:border-red-500":
                        field().state.meta.errors.length > 0,
                    }}
                  />
                  <FieldInfo
                    field={field()}
                    helpText={t(
                      "onboarding.steps.experience.form.startDateHelp",
                    )}
                  />
                </FieldContainer>
              )}
            </form.Field>

            <form.Field
              name="end_date"
              validators={{
                onChange: ({ value }) => {
                  if (value) {
                    if (startDate()) {
                      const startDateObj = startDate()!;
                      const endDateObj = value;
                      if (endDateObj < startDateObj) {
                        return t(
                          "onboarding.steps.experience.form.endDateHelp",
                        );
                      }
                    }
                  }
                },
                onBlur: ({ value }) => {
                  if (!isCurrent() && !value) {
                    return t(
                      "onboarding.steps.experience.form.endDateRequired",
                    );
                  }
                  return undefined;
                },
              }}
            >
              {(field) => (
                <FieldContainer>
                  <FieldContainerLabel for="end_date">
                    {t("onboarding.steps.experience.form.endDate")}
                  </FieldContainerLabel>
                  <DatePicker
                    id="end_date"
                    disabled={!!isCurrent()}
                    value={field().state.value ?? undefined}
                    minDate={startDate() ?? undefined}
                    onChange={(date) => {
                      if (date) {
                        field().handleChange(date);
                      }
                    }}
                    classList={{
                      "[&_button]:border-red-500":
                        field().state.meta.errors.length > 0,
                    }}
                  />
                  <FieldInfo
                    field={field()}
                    helpText={t("onboarding.steps.experience.form.endDateHelp")}
                  />
                </FieldContainer>
              )}
            </form.Field>
          </div>

          <form.Field name="is_current">
            {(field) => (
              <div class="flex items-center gap-2 w-full">
                <Switch
                  name={field().name}
                  checked={field().state.value ?? false}
                  onChange={(checked) => {
                    field().handleChange(checked ?? false);
                    if (checked) {
                      field().form.setFieldValue("end_date", null);
                    }
                  }}
                >
                  <SwitchControl>
                    <SwitchThumb />
                  </SwitchControl>
                </Switch>
                <label for="is_current">
                  {t("onboarding.steps.experience.form.current")}
                </label>
              </div>
            )}
          </form.Field>
          <form.Field name="description">
            {(field) => (
              <TextFieldRoot>
                <TextFieldLabel for="description">
                  {t("onboarding.steps.experience.form.description")}
                </TextFieldLabel>
                <TextArea
                  autocomplete="description"
                  id="description"
                  name={field().name}
                  value={field().state.value ?? ""}
                  onBlur={field().handleBlur}
                  onInput={(e) => field().handleChange(e.currentTarget.value)}
                  class="min-h-[100px]"
                />
                <TextFieldDescription>
                  {t("onboarding.steps.experience.form.descriptionHelp")}
                </TextFieldDescription>
                <Show when={field().state.meta.errors.length > 0}>
                  <TextFieldErrorMessage>
                    {field().state.meta.errors.join(", ")}
                  </TextFieldErrorMessage>
                </Show>
              </TextFieldRoot>
            )}
          </form.Field>
          <form.Subscribe
            selector={(state) => {
              return {
                canSubmit: state.canSubmit,
                isSubmitting: state.isSubmitting,
                isValid: state.isValid,
                isDirty:
                  state.values.job_title !== "" &&
                  state.values.company_name !== "" &&
                  state.values.start_date != null,
              };
            }}
          >
            {(state) => (
              <Button
                class="mt-8 w-full"
                onClick={form.handleSubmit}
                disabled={
                  !state().isValid || state().isSubmitting || !state().isDirty
                }
              >
                {state().isSubmitting ? t("common.saving") : t("common.save")}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </DialogContent>
    </Dialog>
  );
};
